/**
 * How a date is written for THIS user.
 *
 * Nothing here knows what the UK or the US does. The field order and the
 * separator are read off the locale at runtime with
 * `Intl.DateTimeFormat.formatToParts`, so a locale nobody has thought about
 * still renders and parses correctly, and there is no table to keep current.
 *
 * Two jobs, deliberately separate:
 *
 *   - DISPLAY (`formatDate`, `formatDateTime`, and the compact
 *     `formatShortDate` = "01/Nov/27") uses an abbreviated month NAME.
 *     "8 Dec 2026" cannot be misread in any locale, which is why most of the
 *     app was already fine — the sites that were wrong were the numeric ones,
 *     and those now render the compact named form rather than any digit order.
 *
 *   - INPUT (`formatNumericDate`, `parseNumericDate`) is the only place a
 *     numeric date survives, because a masked text field cannot hold a month
 *     name. These two are inverses and MUST change together: rendering
 *     `08/12/2026` and parsing it back as `dd/MM` is self-consistent, but
 *     rendering it US-first while still parsing it UK-first turns 8 December
 *     into 12 August on the next save, with no error anywhere.
 *
 * A date whose day AND month are both <= 12 is the only shape that can
 * transpose like that. It is the canonical case in every test.
 *
 * `toMachineDate` is locale-INDEPENDENT on purpose. Form binding keys and
 * comparison values must not move with the user's locale; only what is rendered
 * to a human does.
 *
 * Mirrored by the Flutter app's `date_format.dart` — change both together.
 */
export function useDates() {
  // The locale to fall back to when we're handed something unusable. This is
  // the product's historic behaviour, so an unknown locale changes nothing.
  const FALLBACK_LOCALE = 'en-GB';

  const orderCache = new Map();

  /** The locale the browser is running in. The only ambient read in the file. */
  const currentLocale = () => {
    if (typeof navigator !== 'undefined' && navigator.language) return navigator.language;
    try {
      return new Intl.DateTimeFormat().resolvedOptions().locale || FALLBACK_LOCALE;
    } catch {
      return FALLBACK_LOCALE;
    }
  };

  /**
   * The locale to actually format in.
   *
   * `new Intl.DateTimeFormat('not-a-locale')` does NOT throw — it quietly
   * resolves to whatever the MACHINE is set to. On a US-configured server or CI
   * box that silently renders US dates for every user, which is the exact bug
   * this module exists to remove. So an unsupported tag is resolved to our own
   * fallback rather than left to the environment.
   */
  const resolveLocale = (locale) => {
    if (!locale) return FALLBACK_LOCALE;
    try {
      const supported = Intl.DateTimeFormat.supportedLocalesOf(locale);
      return supported.length > 0 ? supported[0] : FALLBACK_LOCALE;
    } catch {
      return FALLBACK_LOCALE;
    }
  };

  /** A formatter for `locale`, or the fallback's when the locale is unusable. */
  const numericFormatter = (locale) =>
    new Intl.DateTimeFormat(resolveLocale(locale), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

  /**
   * The parts of a numeric date in the order this locale writes them, plus the
   * separator between them. Everything else in the file is built from this.
   */
  const localeParts = (locale) => {
    const key = resolveLocale(locale);
    if (orderCache.has(key)) return orderCache.get(key);

    // A date with distinct day/month values so the parts can't be confused.
    const parts = numericFormatter(key).formatToParts(new Date(Date.UTC(2026, 11, 8, 12)));
    const order = parts.filter((p) => p.type !== 'literal').map((p) => p.type);
    const separator = parts.find((p) => p.type === 'literal')?.value ?? '/';

    const resolved = {order, separator};
    orderCache.set(key, resolved);
    return resolved;
  };

  /** Whether the day, month or year is written first, as an ordered list. */
  const dateFieldOrder = (locale) => localeParts(locale).order;

  /** The character this locale puts between the fields. */
  const dateSeparator = (locale) => localeParts(locale).separator;

  /**
   * What the input field should SAY it wants — "DD/MM/YYYY" or "MM/DD/YYYY".
   * A masked field that doesn't declare its order forces the user to guess it
   * from whatever value is already in the box, which is exactly how the wrong
   * date gets typed.
   */
  const dateInputPlaceholder = (locale) => {
    const {order, separator} = localeParts(locale);
    const token = {day: 'dd', month: 'mm', year: 'yyyy'};
    return order.map((part) => token[part]).join(separator);
  };

  /** The maska pattern matching `dateInputPlaceholder`, field for field. */
  const dateInputMask = (locale) => {
    const {order, separator} = localeParts(locale);
    const token = {day: '##', month: '##', year: '####'};
    return order.map((part) => token[part]).join(separator);
  };

  /** A Date from anything we might be handed, or null when it isn't one. */
  const asDate = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  /**
   * CLDR separates the time from AM/PM with a NARROW NO-BREAK SPACE (U+202F).
   * Dart's `intl` emits it today and the browser's ICU emits a plain space for
   * the same locale, so the app and the web would render US times as different
   * byte sequences that look identical on screen — breaking string equality,
   * snapshot comparison and any search over the rendered text.
   *
   * The character moved once already (CLDR 42), so normalising here is also
   * protection against the browser adopting it in a future ICU.
   */
  const normaliseSpaces = (value) => value.replace(/[\u202F\u00A0]/g, ' ');

  const formatWith = (value, locale, options) => {
    const date = asDate(value);
    if (date === null) return null;
    return normaliseSpaces(
      new Intl.DateTimeFormat(resolveLocale(locale), options).format(date),
    );
  };

  /** "8 Dec 2026" / "Dec 8, 2026" — a named month, so it cannot be misread. */
  const formatDate = (value, locale) =>
    formatWith(value, locale, {day: 'numeric', month: 'short', year: 'numeric'});

  /**
   * "8 December 2026" / "December 8, 2026" — the month in full, for a date in a
   * sentence ("You keep it until 17 November 2026"). The API writes its own
   * lines the same way, so a web sentence beside one reads alike.
   */
  const formatLongDate = (value, locale) =>
    formatWith(value, locale, {day: 'numeric', month: 'long', year: 'numeric'});

  /**
   * "8 Dec" / "Dec 8" — a named month and no year, for a chat stamp. Mirrors
   * the app's TimeAgo, which shows no year either.
   */
  const formatDayMonth = (value, locale) =>
    formatWith(value, locale, {day: 'numeric', month: 'short'});

  /** "Tue 8 Dec" / "Tue, Dec 8" — the chat's day divider (the app's 'EEE d MMM'). */
  const formatWeekdayDayMonth = (value, locale) =>
    formatWith(value, locale, {weekday: 'short', day: 'numeric', month: 'short'});

  /**
   * "Mon 25 Aug 2025" / "Mon, Aug 25, 2025": the chat's day divider for a day
   * more than 12 months ago. Composed from the weekday and `formatDate`, joined
   * by whatever the locale puts after the weekday WITHOUT a year, so en-GB stays
   * "Mon 25 Aug 2025" (Intl alone writes "Mon, 25 Aug 2025" once a year is in).
   * Mirrors the app's 'EEE d MMM y'.
   */
  const formatWeekdayDate = (value, locale) => {
    const date = asDate(value);
    if (date === null) return null;
    const resolved = resolveLocale(locale);
    const parts = new Intl.DateTimeFormat(resolved, {weekday: 'short', day: 'numeric', month: 'short'})
      .formatToParts(date);
    const at = parts.findIndex((part) => part.type === 'weekday');
    const weekday = parts[at]?.value;
    if (weekday === undefined) return formatDate(date, locale);
    const next = parts[at + 1];
    const separator = next?.type === 'literal' ? next.value : ' ';
    return normaliseSpaces(`${weekday}${separator}${formatDate(date, locale)}`);
  };

  /** "15:30" / "3:30 PM" — the locale decides whether the clock is 24-hour. */
  const formatTime = (value, locale) =>
    formatWith(value, locale, {hour: 'numeric', minute: '2-digit'});

  /**
   * Composed from the two halves rather than asked of Intl as one, so the
   * separator stays stable across ICU versions and platforms.
   */
  const formatDateTime = (value, locale) => {
    const date = formatDate(value, locale);
    if (date === null) return null;
    return `${date}, ${formatTime(value, locale)}`;
  };

  /**
   * "01/Nov/27" — the compact DISPLAY date for tables, chips and cells.
   *
   * Day-first with a short English month name and a two-digit year: it fits
   * where a numeric date fitted, and it cannot be misread in any locale, so it
   * is deliberately the same for every reader (only the clock in
   * `formatShortDateTime` follows the viewer). Mirrored by Flutter's
   * `shortDate()`.
   */
  const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const formatShortDate = (value) => {
    const date = asDate(value);
    if (date === null) return null;
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(date.getDate())}/${SHORT_MONTHS[date.getMonth()]}/${pad(date.getFullYear() % 100)}`;
  };

  /** "01/Nov/27 15:30" / "01/Nov/27 3:30 PM" — short date plus the locale's clock. */
  const formatShortDateTime = (value, locale) => {
    const date = formatShortDate(value);
    if (date === null) return null;
    return `${date} ${formatTime(value, locale)}`;
  };

  /**
   * "08/12/2026" / "12/08/2026" — for the editable input ONLY. Both fields are
   * padded so the value always fills the mask.
   */
  const formatNumericDate = (value, locale) => {
    const date = asDate(value);
    if (date === null) return null;
    return numericFormatter(locale).format(date);
  };

  /**
   * "08/12/2026 15:30" / "12/08/2026 3:30 PM" — the numeric date in the
   * locale's order plus the locale's clock. For the display sites that used
   * `dd/MM/yyyy HH:mm`: numeric stays numeric (they are tight UI), only the
   * ORDER and the clock follow the viewer.
   */
  const formatNumericDateTime = (value, locale) => {
    const date = formatNumericDate(value, locale);
    if (date === null) return null;
    return `${date} ${formatTime(value, locale)}`;
  };

  /**
   * The inverse of `formatNumericDate`, read in the order this locale writes.
   *
   * Returns local midnight as a UTC ISO string, which is what the API has
   * always been sent — this change is about the FORMAT a human types, not about
   * what goes on the wire.
   *
   * A date that does not exist is refused rather than rolled over: JS would
   * turn 31 February into 3 March, and silently moving an admin's typo to a
   * real date is worse than making them retype it.
   */
  const parseNumericDate = (text, locale) => {
    if (typeof text !== 'string') return null;

    const tokens = text.split(/\D+/).filter((token) => token !== '');
    const {order} = localeParts(locale);
    if (tokens.length !== order.length) return null;

    const fields = {};
    order.forEach((part, index) => {
      fields[part] = tokens[index];
    });

    // A four-digit year is required — the mask asks for one, and a two-digit
    // year is a second ambiguity we have no reason to take on.
    if (!/^\d{4}$/.test(fields.year ?? '')) return null;

    const year = Number(fields.year);
    const month = Number(fields.month);
    const day = Number(fields.day);
    if (!Number.isInteger(month) || !Number.isInteger(day)) return null;

    const date = new Date(year, month - 1, day);
    // Rebuilding must give back what went in, or the date wasn't real.
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return null;
    }

    return date.toISOString();
  };

  /**
   * "2026-12-08", always, whatever the locale. Uses LOCAL calendar fields, so
   * it names the same day the user is looking at rather than the UTC day, which
   * can differ either side of midnight.
   */
  const toMachineDate = (value) => {
    const date = asDate(value);
    if (date === null) return null;
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  };

  /**
   * The instant a day STARTS where the reader is — the same value the field
   * commits when they type that day, so a seeded date and a typed one are the
   * same shape.
   *
   * Seeding needs this because a bare "2026-08-18" is not a day, it is UTC
   * midnight: `new Date("2026-08-18")` is defined to parse as UTC, and the
   * numeric formatter renders in the reader's own zone. Anywhere west of
   * Greenwich that lands on the evening BEFORE, so a create form seeded with
   * "today" opened in New York offering yesterday. It reads as a plausible
   * date, which is the failure mode this whole module exists to remove.
   *
   * Takes an instant (a Date, or a luxon DateTime via .toJSDate()), never a
   * yyyy-MM-dd string — that string has already lost the zone.
   */
  const startOfDayIso = (value) => {
    const date = asDate(value);
    if (date === null) return null;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).toISOString();
  };

  /**
   * The text to put in a masked date field for a stored value.
   *
   * Paired with `dateInputCommit`. These two live here rather than in
   * `TapDatePicker.vue` because they are the part that can actually be wrong, and
   * the repo has no component-test setup — the component itself is wiring.
   */
  const dateInputDisplay = (value, locale) => formatNumericDate(value, locale) ?? '';

  /**
   * What a field's current text means.
   *
   * Runs on every keystroke, so a half-typed date has to be `incomplete` rather
   * than `invalid`: flagging an error at "08/12/20" shouts at someone who is
   * still typing the year. Only a full-length entry can be judged real or not.
   *
   *   cleared   - the box is empty; commit null and forget the old value
   *   complete  - a real date; `iso` is set and safe to store
   *   invalid   - full length but not a date that exists (31 February)
   */
  const dateInputCommit = (text, locale) => {
    const raw = (text ?? '').trim();
    if (raw === '') {
      return {iso: null, complete: false, invalid: false, cleared: true};
    }

    const digits = raw.replace(/\D/g, '');
    const expected = dateInputMask(locale).replace(/[^#]/g, '').length;
    if (digits.length < expected) {
      return {iso: null, complete: false, invalid: false, cleared: false};
    }

    const iso = parseNumericDate(raw, locale);
    if (iso === null) {
      return {iso: null, complete: false, invalid: true, cleared: false};
    }
    return {iso, complete: true, invalid: false, cleared: false};
  };

  /**
   * The local wall-clock time of an instant, as "HH:mm".
   *
   * Several forms pair a `TapDatePicker` with a separate time select. They used to
   * split and rejoin by string-concatenating a display date with a time and
   * re-parsing the result against a hardcoded pattern, which is what coupled
   * them to `dd/MM/yyyy`.
   */
  const timeOfDay = (value) => {
    const date = asDate(value);
    if (date === null) return null;
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  /**
   * The same day as `value`, at the local wall-clock time `time` ("HH:mm").
   * A missing or unreadable time leaves the instant untouched.
   */
  const withTimeOfDay = (value, time) => {
    const date = asDate(value);
    if (date === null) return null;

    const match = /^(\d{1,2}):(\d{2})$/.exec((time ?? '').trim());
    if (match === null) return date.toISOString();

    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (hours > 23 || minutes > 59) return date.toISOString();

    const merged = new Date(date);
    merged.setHours(hours, minutes, 0, 0);
    return merged.toISOString();
  };

  return {
    currentLocale,
    dateInputDisplay,
    dateInputCommit,
    timeOfDay,
    withTimeOfDay,
    dateFieldOrder,
    dateSeparator,
    dateInputPlaceholder,
    dateInputMask,
    formatDate,
    formatLongDate,
    formatDayMonth,
    formatWeekdayDayMonth,
    formatWeekdayDate,
    formatTime,
    formatDateTime,
    formatNumericDate,
    formatNumericDateTime,
    formatShortDate,
    formatShortDateTime,
    parseNumericDate,
    toMachineDate,
    startOfDayIso,
  };
}
